import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/ApiService';
import type {
  EmailDeliveryRow,
  EmailFlagKey,
  EmailFlagsMap,
  EmailMailStatus,
  EmailOpsStatus,
  EmailTemplatePreview,
} from '../services/api/adminEmail';

type TabKey = 'status' | 'flags' | 'delivery' | 'previews';

const FLAG_LABELS: Record<EmailFlagKey, { label: string; hint: string }> = {
  transactional_email_enabled: {
    label: 'Transactional email (platform flag)',
    hint: 'Password reset, verification, account notices. Also on when MAIL_ENABLED=1 in api/.env.',
  },
  email_verification_enabled: {
    label: 'Email verification on signup',
    hint: 'Send verify email to new accounts. Keep off until Resend delivery is healthy.',
  },
  email_verification_required: {
    label: 'Require verified email',
    hint: 'Stricter product gate. Enable only after verification email is proven.',
  },
  sensitive_action_email_gate_enabled: {
    label: 'Sensitive-action email gate',
    hint: 'Blocks some sensitive actions until verified. Enable last.',
  },
  marketing_email_enabled: {
    label: 'Marketing email',
    hint: 'Keep off until legal/unsubscribe requirements are confirmed.',
  },
  cookie_consent_enabled: {
    label: 'Cookie consent banner',
    hint: 'Web cookie preference / consent UI.',
  },
};

/**
 * Super-admin email ops: provider status (no secrets), flags, delivery log, template previews.
 */
export const AdminEmail: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useState<TabKey>('status');
  const [status, setStatus] = useState<EmailMailStatus | null>(null);
  const [flags, setFlags] = useState<EmailFlagsMap | null>(null);
  const [effective, setEffective] = useState<EmailOpsStatus['effective'] | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [delivery, setDelivery] = useState<EmailDeliveryRow[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [previews, setPreviews] = useState<EmailTemplatePreview[]>([]);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [flagBusy, setFlagBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (tab === 'status' || tab === 'flags') {
        const data = await apiService.getEmailOpsStatus();
        setStatus(data.status);
        setFlags(data.flags);
        setEffective(data.effective);
        setNotes(Array.isArray(data.notes) ? data.notes : []);
      } else if (tab === 'delivery') {
        setDelivery(
          await apiService.getEmailDeliveryLog({
            limit: 50,
            status: statusFilter || undefined,
          })
        );
      } else {
        setPreviews(await apiService.getEmailTemplatePreviews());
      }
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Email ops backend is not reachable.');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleFlag = async (key: EmailFlagKey, enabled: boolean) => {
    setFlagBusy(key);
    try {
      const data = await apiService.setEmailFlag(key, enabled);
      setFlags(data.flags);
      setEffective((prev) =>
        prev
          ? { ...prev, transactional_outbound: data.effective.transactional_outbound }
          : { transactional_outbound: data.effective.transactional_outbound }
      );
      showToast(`${FLAG_LABELS[key].label} ${enabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to update flag', 'error');
    } finally {
      setFlagBusy(null);
    }
  };

  const selectedPreview = previews.find((p) => p.template_key === previewKey) || previews[0] || null;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Email"
        description="Resend provider status, rollout flags, delivery log, and template previews. API keys stay in api/.env only."
        actions={
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      <div role="tablist" aria-label="Email admin views" className="flex flex-wrap gap-1">
        {(
          [
            ['status', 'Provider status'],
            ['flags', 'Flags'],
            ['delivery', 'Delivery log'],
            ['previews', 'Templates'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
              tab === key
                ? 'bg-orange-500/15 text-orange-300 border border-orange-500/35'
                : 'text-gray-400 border border-transparent hover:bg-white/[0.04]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'delivery' ? (
        <div className="flex flex-wrap gap-2 items-center">
          <label className="sr-only" htmlFor="email-delivery-status">
            Filter by status
          </label>
          <select
            id="email-delivery-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input max-w-[220px] min-h-[44px]"
          >
            <option value="">All statuses</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="logged">logged</option>
            <option value="suppressed">suppressed</option>
          </select>
        </div>
      ) : null}

      {loading ? (
        <p className="text-gray-400" role="status">
          Loading…
        </p>
      ) : loadError ? (
        <AdminQueueState
          variant="error"
          title="Could not load email ops"
          description={loadError}
          onRetry={() => void load()}
        />
      ) : tab === 'status' && status ? (
        <div className="space-y-4 max-w-2xl">
          <ul className="space-y-2 list-none">
            <StatusRow label="MAIL_ENABLED" value={status.mail_enabled ? 'On' : 'Off'} ok={status.mail_enabled} />
            <StatusRow label="Provider" value={status.provider || '—'} ok={status.provider === 'resend'} />
            <StatusRow
              label="API key configured"
              value={status.api_key_configured ? 'Yes' : 'No'}
              ok={status.api_key_configured}
            />
            <StatusRow label="From" value={`${status.from_name} <${status.from_address}>`} ok />
            <StatusRow label="Reply-To" value={status.reply_to || '—'} ok={Boolean(status.reply_to)} />
            <StatusRow
              label="Operator alerts"
              value={status.operator_alerts_configured ? 'Configured' : 'Not set'}
              ok={status.operator_alerts_configured}
            />
            <StatusRow
              label="Transactional outbound (effective)"
              value={effective?.transactional_outbound ? 'Active' : 'Off'}
              ok={Boolean(effective?.transactional_outbound)}
            />
          </ul>
          {notes.length > 0 ? (
            <div className="form-section text-sm text-gray-400 space-y-1">
              {notes.map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          ) : null}
          {!/(^|\.)biltspec\.com$/i.test(
            String(status.from_address.split('@')[1] || '').trim()
          ) ? (
            <div className="form-section text-sm text-amber-200/90 space-y-1" role="status">
              <p>
                From address is not on biltspec.com. Update server{' '}
                <code className="text-amber-100">api/.env</code> so{' '}
                <code className="text-amber-100">MAIL_FROM_ADDRESS</code> is{' '}
                <code className="text-amber-100">noreply@biltspec.com</code> (and Reply-To{' '}
                <code className="text-amber-100">support@biltspec.com</code>).
              </p>
            </div>
          ) : null}
          <p className="text-sm text-gray-500">
            To wire Resend: set <code className="text-gray-300">MAIL_PROVIDER=resend</code>,{' '}
            <code className="text-gray-300">MAIL_API_KEY</code>, and From/Reply on the server{' '}
            <code className="text-gray-300">api/.env</code>, then test Forgot password and check the Resend
            dashboard.
          </p>
        </div>
      ) : tab === 'flags' && flags ? (
        <ul className="space-y-2 list-none max-w-2xl">
          {(Object.keys(FLAG_LABELS) as EmailFlagKey[]).map((key) => {
            const enabled = Boolean(flags[key]);
            const meta = FLAG_LABELS[key];
            return (
              <li key={key} className="form-section flex items-center justify-between gap-4 !py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">{meta.label}</p>
                  <p className="text-xs text-gray-500 font-mono">{key}</p>
                  <p className="text-xs text-gray-400 mt-1">{meta.hint}</p>
                </div>
                <Button
                  type="button"
                  variant={enabled ? 'primary' : 'secondary'}
                  size="sm"
                  loading={flagBusy === key}
                  onClick={() => void toggleFlag(key, !enabled)}
                  aria-pressed={enabled}
                >
                  {enabled ? 'On' : 'Off'}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : tab === 'delivery' ? (
        delivery.length === 0 ? (
          <AdminQueueState
            variant="empty"
            title="No delivery rows yet"
            description="Successful and failed sends appear here after Forgot password or verification emails run."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <caption className="sr-only">Email delivery log</caption>
              <thead className="text-xs uppercase text-gray-500 border-b border-white/[0.08]">
                <tr>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    When
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Template
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Status
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Provider
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody>
                {delivery.map((row) => (
                  <tr key={row.id} className="border-b border-white/[0.05] align-top">
                    <td className="py-3 pr-3 text-gray-400 whitespace-nowrap">
                      {row.created_at || '—'}
                    </td>
                    <td className="py-3 pr-3 text-white font-mono text-xs">{row.template_key}</td>
                    <td className="py-3 pr-3">
                      <span
                        className={
                          row.status === 'sent' || row.status === 'logged'
                            ? 'text-emerald-300'
                            : row.status === 'failed'
                              ? 'text-rose-300'
                              : 'text-gray-300'
                        }
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-gray-400">{row.provider || '—'}</td>
                    <td className="py-3 text-gray-400 max-w-xs">
                      <span className="block truncate" title={row.subject}>
                        {row.subject}
                      </span>
                      {row.error_message ? (
                        <span className="block text-rose-300/90 text-xs mt-1" title={row.error_message}>
                          {row.error_message}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : tab === 'previews' ? (
        previews.length === 0 ? (
          <AdminQueueState variant="empty" title="No templates" description="Template renderer returned no previews." />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[14rem_1fr]">
            <ul className="space-y-1 list-none">
              {previews.map((p) => {
                const active = (selectedPreview?.template_key || '') === p.template_key;
                return (
                  <li key={p.template_key}>
                    <button
                      type="button"
                      onClick={() => setPreviewKey(p.template_key)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm min-h-[44px] cursor-pointer border ${
                        active
                          ? 'bg-white/[0.06] text-white border-white/20'
                          : 'text-gray-300 border-transparent hover:bg-white/[0.04]'
                      }`}
                    >
                      {p.template_key}
                    </button>
                  </li>
                );
              })}
            </ul>
            {selectedPreview ? (
              <div className="form-section space-y-3 min-w-0">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">Subject</p>
                  <p className="text-white mt-1">{selectedPreview.subject}</p>
                </div>
                <div className="rounded-lg border border-white/[0.08] bg-white overflow-hidden min-h-[14rem]">
                  <iframe
                    title={`Preview ${selectedPreview.template_key}`}
                    srcDoc={selectedPreview.html}
                    className="w-full h-[28rem] border-0"
                    sandbox=""
                  />
                </div>
                <pre className="text-xs text-gray-400 whitespace-pre-wrap overflow-x-auto max-h-48">
                  {selectedPreview.text}
                </pre>
              </div>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );
};

function StatusRow({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <li className="form-section flex items-center justify-between gap-4 !py-3">
      <p className="text-sm text-gray-400">{label}</p>
      <p className={`text-sm font-medium ${ok ? 'text-emerald-300' : 'text-amber-200'}`}>{value}</p>
    </li>
  );
}

export default AdminEmail;
